import {useFormField, type ValidationFunc} from '@nfq/react-form';

/**
 * A custom hook for managing a tag-based checkbox field in a form.
 * This hook maintains the state of a form field representing multiple selectable tags.
 * It provides a handler to update the field's value when checkboxes are checked or unchecked.
 *
 * @param fieldName     The name of the form field associated with the tag selection.
 * @param initialValue  An array of initially selected tag values.
 * @param isSingle      A boolean indicating whether only one tag can be selected at a time (default: `false`).
 * @param keepOnUmnount A boolean indicating whether to keep the field value on component unmount (default: `false`).
 * @param validation    An optional validation function to validate the input value.
 * @returns An object containing:
 * - `handleChange`: A function to update the field state based on checkbox selection.
 * - `value`: The current array of selected tag values.
 *
 * @example
 * ```tsx
 * const { handleChange, value } = useTagCloud("categories", ["tag1"]);
 *
 * // Handling a checkbox selection
 * handleChange("tag2", true); // Adds "tag2" to the selected values
 * handleChange("tag1", false); // Removes "tag1" from the selected values
 * ```
 */
export const useTagCloud = (
    fieldName: string,
    initialValue: string[],
    isSingle: boolean,
    keepOnUmnount: boolean,
    validation: ValidationFunc
) => {
    const {
        error,
        handleFieldChange,
        value
    } = useFormField(fieldName, initialValue, initialValue, validation, keepOnUmnount);

    /**
     * `handleChange` is a function that updates the state of the field in a form based on a checkbox's state.
     * If the checkbox is checked, the field's state is updated to include the checkbox's value.
     * If the checkbox is unchecked, the checkbox's value is removed from the field's state.
     *
     * @param fieldValue The value of the checkbox.
     * @param isChecked  The current state of the checkbox.
     */
    const handleChange = (fieldValue: string, isChecked: boolean) => {
        if (isSingle) {
            handleFieldChange([fieldValue]);

            return;
        }

        if (isChecked) {
            handleFieldChange([...value, fieldValue]);
        } else {
            handleFieldChange((value).filter(filterValue => filterValue !== fieldValue));
        }
    };

    return {
        error,
        handleChange,
        value
    };
};